/**
 * Login.tsx — Admin login page (T-04-32 / ARCH §10.3).
 *
 * Flow:
 *   1. POST /auth/login → sets httpOnly session cookie
 *   2. GET /auth/me verifies role=admin; chatters are rejected + shown error
 *   3. On success → redirect to /
 *
 * Security:
 *   - form autocomplete="current-password" for password field
 *   - Errors shown verbatim from the API (never echoes passwords back)
 *   - Non-admin role gets a clear "Access denied" message
 */

import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, ApiRequestError } from '../lib/api-client.js';
import { Button } from '@crm/ui';
import { Input } from '@crm/ui';
import { Card, CardHeader, CardTitle, CardContent } from '@crm/ui';
import { ThemeSwitch } from '@crm/ui';

export function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);

  // One-time post-action banners (SC-1 accept-invite success, SC-4 reset success)
  const invitedBanner = params.get('invited') === '1';
  const resetBanner = params.get('reset') === '1';

  const loginMut = useMutation({
    mutationFn: async () => {
      const me = await authApi.login({ email, password });
      // T-04-32: reject non-admin sessions immediately
      if (me.role !== 'admin') {
        await authApi.logout().catch(() => {});
        throw new Error('Access denied: admin role required.');
      }
      return me;
    },
    onSuccess: (me) => {
      // Cache the /auth/me result
      queryClient.setQueryData(['me'], me);
      navigate('/', { replace: true });
    },
    onError: (err) => {
      if (err instanceof ApiRequestError) {
        setErrorMsg(
          typeof err.body.message === 'string'
            ? err.body.message
            : err.body.message.join('; '),
        );
      } else {
        setErrorMsg((err as Error).message ?? 'Login failed');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    loginMut.mutate();
  };

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeSwitch />
      </div>
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-xl font-bold text-text">MaloumDash</h1>
          <p className="text-sm text-text-muted mt-1">Admin console</p>
        </div>

        {/* Post-action banners (one-time, from ?invited=1 or ?reset=1 URL param) */}
        {invitedBanner && (
          <p className="text-sm text-success mb-4 text-center" role="status">
            Account created. Sign in to continue.
          </p>
        )}
        {resetBanner && (
          <p className="text-sm text-success mb-4 text-center" role="status">
            Password reset successfully. Sign in with your new password.
          </p>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <Input
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loginMut.isPending}
              />
              <Input
                label="Password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loginMut.isPending}
              />

              {errorMsg && (
                <p className="text-sm text-danger" role="alert">
                  {errorMsg}
                </p>
              )}

              <Button
                type="submit"
                loading={loginMut.isPending}
                className="w-full justify-center mt-2"
              >
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Forgot password link (D-09-19 / SC-4) */}
        <p className="text-center text-xs text-text-muted mt-3">
          <a href="/reset-password" className="text-accent hover:underline">
            Forgot password?
          </a>
        </p>
      </div>
    </div>
  );
}
